import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { numberSorter } from 'utils/sorters';
import {
  ColumnHeader,
  commonColumns,
  CsatCell,
  NumberCell,
  projectNameColumn,
} from '../../common';
import { ProjectsReportRecord } from '../../types';

interface Params {
  noOfRows: number;
}
export const usePhasesAndTasksColumns = ({
  noOfRows,
}: Params): ColumnsType<ProjectsReportRecord> => {
  const translate = useTranslate();

  return [
    projectNameColumn(noOfRows, translate),
    {
      titleText: useTranslation('reports.columns.phases'),
      key: 'phases',
      render: (_, { phasesSummary }: ProjectsReportRecord) => (
        <NumberCell value={phasesSummary?.count} />
      ),
      sorter: (
        { phasesSummary: a }: ProjectsReportRecord,
        { phasesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.count, b?.count),
    },
    {
      titleText: useTranslation('reports.columns.phases.completed_on_time'),
      key: 'phasesCompletedOnTime',
      render: (_, { phasesSummary }: ProjectsReportRecord) => (
        <NumberCell value={phasesSummary?.completedOnTimeCount} />
      ),
      sorter: (
        { phasesSummary: a }: ProjectsReportRecord,
        { phasesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedOnTimeCount, b?.completedOnTimeCount),
    },
    {
      titleText: useTranslation('reports.columns.phases.completed_late'),
      key: 'phasesCompletedLate',
      render: (_, { phasesSummary }: ProjectsReportRecord) => (
        <NumberCell value={phasesSummary?.completedLateCount} />
      ),
      sorter: (
        { phasesSummary: a }: ProjectsReportRecord,
        { phasesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedLateCount, b?.completedLateCount),
    },
    {
      titleText: useTranslation('reports.columns.tasks'),
      key: 'tasks',
      render: (_, { tasksSummary }: ProjectsReportRecord) => (
        <NumberCell value={tasksSummary?.count} />
      ),
      sorter: (
        { tasksSummary: a }: ProjectsReportRecord,
        { tasksSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.count, b?.count),
    },
    {
      titleText: useTranslation('reports.columns.tasks.completed_on_time'),
      key: 'tasksCompletedOnTime',
      render: (_, { tasksSummary }: ProjectsReportRecord) => (
        <NumberCell value={tasksSummary?.completedOnTimeCount} />
      ),
      sorter: (
        { tasksSummary: a }: ProjectsReportRecord,
        { tasksSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedOnTimeCount, b?.completedOnTimeCount),
    },
    {
      titleText: useTranslation('reports.columns.tasks.completed_late'),
      key: 'tasksCompletedLate',
      render: (_, { tasksSummary }: ProjectsReportRecord) => (
        <NumberCell value={tasksSummary?.completedLateCount} />
      ),
      sorter: (
        { tasksSummary: a }: ProjectsReportRecord,
        { tasksSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedLateCount, b?.completedLateCount),
    },
    {
      titleText: useTranslation('reports.columns.tasks.not_completed'),
      key: 'tasksNotCompleted',
      render: (_, { tasksSummary }: ProjectsReportRecord) => (
        <NumberCell value={tasksSummary?.incompleteCount} />
      ),
      sorter: (
        { tasksSummary: a }: ProjectsReportRecord,
        { tasksSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.incompleteCount, b?.incompleteCount),
    },
    {
      titleText: useTranslation('reports.columns.milestones'),
      key: 'milestones',
      render: (_, { milestonesSummary }: ProjectsReportRecord) => (
        <NumberCell value={milestonesSummary?.count} />
      ),
      sorter: (
        { milestonesSummary: a }: ProjectsReportRecord,
        { milestonesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.count, b?.count),
    },
    {
      titleText: useTranslation('reports.columns.milestones.completed_on_time'),
      key: 'milestonesCompletedOnTime',
      render: (_, { milestonesSummary }: ProjectsReportRecord) => (
        <NumberCell value={milestonesSummary?.completedOnTimeCount} />
      ),
      sorter: (
        { milestonesSummary: a }: ProjectsReportRecord,
        { milestonesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedOnTimeCount, b?.completedOnTimeCount),
    },
    {
      titleText: useTranslation('reports.columns.milestones.completed_late'),
      key: 'milestonesCompletedLate',
      render: (_, { milestonesSummary }: ProjectsReportRecord) => (
        <NumberCell value={milestonesSummary?.completedLateCount} />
      ),
      sorter: (
        { milestonesSummary: a }: ProjectsReportRecord,
        { milestonesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.completedLateCount, b?.completedLateCount),
    },
    {
      titleText: useTranslation('reports.columns.milestones.not_completed'),
      key: 'milestonesNotCompleted',
      render: (_, { milestonesSummary }: ProjectsReportRecord) => (
        <NumberCell value={milestonesSummary?.incompleteCount} />
      ),
      sorter: (
        { milestonesSummary: a }: ProjectsReportRecord,
        { milestonesSummary: b }: ProjectsReportRecord,
      ) => numberSorter(a?.incompleteCount, b?.incompleteCount),
    },
    {
      titleText: useTranslation('reports.columns.average_csat_received'),
      key: 'csatReceived',
      render: (_, { averageCSAT }: ProjectsReportRecord) => (
        <CsatCell value={averageCSAT} />
      ),
      sorter: (
        { averageCSAT: a }: ProjectsReportRecord,
        { averageCSAT: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.non_template_tasks'),
      key: 'nonTemplateTasks',
      render: (_, { nonTemplateTaskCount }: ProjectsReportRecord) => (
        <NumberCell value={nonTemplateTaskCount} />
      ),
      sorter: (
        { nonTemplateTaskCount: a }: ProjectsReportRecord,
        { nonTemplateTaskCount: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    ...commonColumns(translate),
  ].map((x) => ({
    showSorterTooltip: false,
    width: '224px',
    title: <ColumnHeader title={x.titleText} description={x.description} />,
    ...x,
  }));
};
